import React, { useState, useEffect, useContext } from 'react';
import { ChevronDown } from 'lucide-react';
import useApi from '../../hooks/useApi';
import { AuthContext } from '../../context/AuthContext';

// EngagementAnalytics Component: Shows job views chart with REAL data
const EngagementAnalytics = () => {
  // Get current user to filter jobs for recruiters
  const { user } = useContext(AuthContext);
  const isRecruiter = user?.userType === 'recruiter';
  
  // Fetch real job data from API
  const { data: apiData, loading, error } = useApi('jobs');
  
  // Extract jobs from API response with safe defaults
  const jobsData = apiData?.data?.jobs || apiData?.jobs || apiData?.data?.data || apiData?.data || apiData || [];
  const allJobs = Array.isArray(jobsData) ? jobsData : [];
  
  // Filter jobs based on user type with safe defaults
  const jobsArray = isRecruiter && user?.id
    ? allJobs.filter(job => job?.recruiterId === user.id)  // Recruiters only see their jobs
    : allJobs;  // Employers see all jobs
  
  const [selectedJob, setSelectedJob] = useState(null);
  const [timeframe, setTimeframe] = useState('Daily');
  const [viewsData, setViewsData] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  
  // Validate viewsData array
  useEffect(() => {
    if (!Array.isArray(viewsData) || viewsData.length !== 7) {
      console.error('[ERROR] Invalid viewsData array:', viewsData);
      setViewsData([0, 0, 0, 0, 0, 0, 0]);
    }
  }, [viewsData]);
  
  // Set initial selected job when data loads
  useEffect(() => {
    if (jobsArray.length > 0 && !selectedJob) {
      const firstJob = jobsArray[0];
      setSelectedJob(firstJob);
      
      // Use real analytics data or default to zeros
      if (firstJob.analytics && firstJob.analytics.dailyViews) {
        const views = firstJob.analytics.dailyViews;
        // Ensure it's an array of 7 numbers
        if (Array.isArray(views) && views.length === 7) {
          setViewsData(views);
        } else {
          console.warn('[WARNING] Invalid dailyViews format, using defaults');
          setViewsData([0, 0, 0, 0, 0, 0, 0]);
        }
      } else {
        console.warn('[WARNING] Job missing analytics data:', firstJob.id, '- using defaults');
        setViewsData([0, 0, 0, 0, 0, 0, 0]);
      }
    }
  }, [jobsArray.length, selectedJob]);
  
  // X-axis labels based on timeframe
  const getXAxisLabels = () => {
    const today = new Date();
    
    if (timeframe === 'Daily') {
      // Daily: Generate last 7 days dynamically
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const labels = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        labels.push(dayNames[date.getDay()]);
      }
      return labels;
    } else if (timeframe === 'Weekly') {
      // Weekly: Generate last 7 weeks dynamically
      const labels = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - (i * 7));
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        labels.push(`${month}/${day}`);
      }
      return labels;
    } else {
      // Monthly: Generate last 7 months dynamically
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const labels = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        labels.push(months[date.getMonth()]);
      }
      return labels;
    }
  };
  
  const xAxisLabels = getXAxisLabels() || [];
  
  // Update data when timeframe or selected job changes
  useEffect(() => {
    if (selectedJob && selectedJob.analytics) {
      const analytics = selectedJob.analytics;
      
      if (timeframe === 'Daily' && analytics.dailyViews) {
        setViewsData(analytics.dailyViews);
      } else if (timeframe === 'Weekly' && analytics.weeklyViews) {
        setViewsData(analytics.weeklyViews);
      } else if (timeframe === 'Monthly' && analytics.monthlyViews) {
        setViewsData(analytics.monthlyViews);
      } else {
        // Fallback to zeros if no analytics data
        console.warn(`[WARNING] No ${timeframe} analytics data for job: ${selectedJob.title}`);
        setViewsData([0, 0, 0, 0, 0, 0, 0]);
      }
    }
  }, [timeframe, selectedJob]);
  
  // Get max value for scaling the chart
  const maxValue = 100; // Fixed max value (0-100 range)
  
  // Generate y-axis labels with 20-unit intervals (6 labels: 100, 80, 60, 40, 20, 0)
  const yLabels = ['100', '80', '60', '40', '20', '0'];
  
  // Handle job selection
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    setShowJobDropdown(false);
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 text-sm">Loading analytics...</div>
        </div>
      </div>
    );
  }
  
  // Show error state without crashing
  if (error || (!loading && !apiData)) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-base font-medium text-gray-800 mb-4">Job Views</h2>
        <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-200 rounded">
          <div className="text-center">
            <p className="text-gray-500 text-sm">Unable to load analytics data</p>
            <p className="text-gray-400 text-xs mt-1">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Show empty state if no jobs
  if (jobsArray.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-base font-medium text-gray-800 mb-4">Job Views</h2>
        <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-200 rounded">
          <div className="text-center">
            <p className="text-gray-500 text-sm">
              {isRecruiter ? "You haven't posted any jobs yet" : "No jobs available"}
            </p>
            <p className="text-gray-400 text-xs mt-1">Analytics will appear here once jobs are posted</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-base font-medium text-gray-800 mr-2">Job Views</h2>
            {/* Job selection dropdown */}
          <div className="relative">
            <button 
              className="flex items-center text-sm border border-gray-300 rounded px-2 py-1 bg-white"
              onClick={() => setShowJobDropdown(!showJobDropdown)}
            >
              <span className="mr-1 text-xs">{selectedJob?.title || 'Select Job'}</span>
              <ChevronDown size={14} />
            </button>
            
            {showJobDropdown && (
              <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-md z-10">
                {jobsArray.length > 0 ? jobsArray.map(job => (
                  <button
                    key={job.id}
                    className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-100 transition-colors"
                    onClick={() => handleJobSelect(job)}
                  >
                    <div className="flex justify-between items-center">
                      <span>{job.title}</span>
                      <span className="text-gray-400 text-[10px]">
                        {job.analytics?.totalViews || 0} views
                      </span>
                    </div>
                  </button>
                )) : (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    {isRecruiter ? 'You haven\'t posted any jobs yet' : 'No jobs available'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center bg-gray-100 rounded-md p-0.5">
          <button
            className={`px-3 py-1 text-xs rounded-md transition-all ${timeframe === 'Daily' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTimeframe('Daily')}
          >
            Days
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md transition-all ${timeframe === 'Weekly' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTimeframe('Weekly')}
          >
            Weeks
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md transition-all ${timeframe === 'Monthly' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setTimeframe('Monthly')}
          >
            Months
          </button>
        </div>
      </div>
      
      {/* Chart */}
      <div className="mt-6">
        <div className="relative h-64">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2">
            {yLabels.map((label, index) => (
              <div key={index} className="leading-3">{label}</div>
            ))}
          </div>
          
          {/* Chart area */}
          <div className="ml-8 h-full relative">
            {/* Horizontal grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between">
              {yLabels.map((_, index) => (
                <div key={index} className="border-b border-gray-200 w-full" style={{ height: '0px' }}></div>
              ))}
            </div>
            
            {/* Bar chart */}
            <div className="absolute inset-0 flex items-end justify-between">
              {Array.isArray(viewsData) && viewsData.length > 0 && viewsData.map((value, index) => {
                // Calculate height as pixels based on the container height (256px = h-64)
                const containerHeight = 256; // h-64 in pixels
                const barHeightPx = value > 0 ? Math.max((value / maxValue) * containerHeight, 4) : 0;
                
                return (
                  <div 
                    key={index} 
                    className="relative flex flex-col items-center justify-end h-full"
                    style={{ width: `${100 / viewsData.length}%` }}
                  >
                    {/* The bar itself */}
                    <div 
                      className="w-4/5 bg-black rounded-t transition-all duration-200 hover:opacity-80"
                      style={{ 
                        height: `${barHeightPx}px`
                      }}
                      title={`${value} views`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* X-axis labels */}
          <div className="ml-8 mt-2 w-full pt-1">
            <div className="flex w-full justify-between">
              {Array.isArray(xAxisLabels) && xAxisLabels.length > 0 && xAxisLabels.map((label, index) => (
                <div key={index} className="text-xs text-gray-500 text-center" style={{width: `${100 / xAxisLabels.length}%`}}>
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementAnalytics;
